/**
 * Recommendation Preview Card Component
 *
 * Compact card for displaying recommendations on the home screen.
 * Shows EXIT/WATCH recommendation type, fund name, brief reason,
 * and suggested action with navigation to full details.
 */

import { Link } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  LogOut,
  Eye,
  ArrowRight,
  RefreshCw,
  TrendingUp,
  Pause,
} from "lucide-react";

// =============================================================================
// Types
// =============================================================================

/**
 * Recommendation types supported by the preview card
 */
export type RecommendationType = "exit" | "watch" | "switch" | "increase-sip" | "hold";

/**
 * Priority levels for recommendations
 */
export type RecommendationPriority = "high" | "medium" | "low";

/**
 * Props for a single recommendation preview
 */
export interface RecommendationPreview {
  /** Unique identifier for the recommendation */
  id: string;
  /** Type of recommendation */
  type: RecommendationType;
  /** Fund name or holding name */
  fundName: string;
  /** Brief reason/explanation (1-2 lines) */
  reason: string;
  /** Suggested action text */
  suggestedAction: string;
  /** Priority level */
  priority: RecommendationPriority;
}

/**
 * Props for the RecommendationPreviewCard component
 */
export interface RecommendationPreviewCardProps {
  /** Recommendation data */
  recommendation: RecommendationPreview;
  /** Additional CSS classes */
  className?: string;
  /** Optional click handler (in addition to navigation) */
  onClick?: () => void;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Configuration for recommendation types
 * Defines visual appearance and icons for each type
 */
const TYPE_CONFIG: Record<
  RecommendationType,
  {
    label: string;
    icon: React.ElementType;
    badgeClassName: string;
    iconClassName: string;
  }
> = {
  exit: {
    label: "EXIT",
    icon: LogOut,
    badgeClassName:
      "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800",
    iconClassName: "text-rose-600 dark:text-rose-400",
  },
  watch: {
    label: "WATCH",
    icon: Eye,
    badgeClassName:
      "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    iconClassName: "text-amber-600 dark:text-amber-400",
  },
  switch: {
    label: "SWITCH",
    icon: RefreshCw,
    badgeClassName:
      "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800",
    iconClassName: "text-sky-600 dark:text-sky-400",
  },
  "increase-sip": {
    label: "INCREASE SIP",
    icon: TrendingUp,
    badgeClassName:
      "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
    iconClassName: "text-emerald-600 dark:text-emerald-400",
  },
  hold: {
    label: "HOLD",
    icon: Pause,
    badgeClassName:
      "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    iconClassName: "text-slate-600 dark:text-slate-400",
  },
};

/**
 * Priority indicator styles
 */
const PRIORITY_INDICATOR: Record<RecommendationPriority, string> = {
  high: "bg-rose-500",
  medium: "bg-amber-500",
  low: "bg-emerald-500",
};

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Type badge with icon
 */
function TypeBadge({ type }: { type: RecommendationType }) {
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-semibold uppercase tracking-wide",
        config.badgeClassName
      )}
    >
      <Icon className={cn("h-3 w-3", config.iconClassName)} />
      {config.label}
    </Badge>
  );
}

/**
 * Priority indicator dot
 */
function PriorityIndicator({
  priority,
  className,
}: {
  priority: RecommendationPriority;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "h-2 w-2 rounded-full",
        PRIORITY_INDICATOR[priority],
        className
      )}
      title={`${priority} priority`}
      aria-label={`${priority} priority`}
    />
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Recommendation Preview Card
 *
 * A compact card designed for displaying recommendations on the home screen.
 * Features:
 * - Visual type badge (EXIT in red, WATCH in amber, etc.)
 * - Fund name display
 * - Truncated reason summary
 * - Action button with navigation
 * - Priority indicator
 *
 * @example
 * ```tsx
 * <RecommendationPreviewCard
 *   recommendation={{
 *     id: "rec-1",
 *     type: "exit",
 *     fundName: "HDFC Small Cap Fund",
 *     reason: "Fund has been underperforming its benchmark for 6 consecutive quarters",
 *     suggestedAction: "Consider exiting this position",
 *     priority: "high",
 *   }}
 * />
 * ```
 */
export function RecommendationPreviewCard({
  recommendation,
  className,
  onClick,
}: RecommendationPreviewCardProps) {
  const { id, type, fundName, reason, suggestedAction, priority } =
    recommendation;

  return (
    <Link
      to="/recommendations/$recommendationId"
      params={{ recommendationId: id }}
      className="block"
      onClick={onClick}
    >
      <Card
        className={cn(
          "group cursor-pointer transition-all duration-200",
          "hover:shadow-md hover:border-primary/20",
          "active:scale-[0.99]",
          className
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Priority Indicator */}
            <PriorityIndicator priority={priority} className="mt-2 shrink-0" />

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Header: Type Badge + Fund Name */}
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1 min-w-0">
                  <TypeBadge type={type} />
                  <h3 className="font-semibold text-foreground truncate">
                    {fundName}
                  </h3>
                </div>
              </div>

              {/* Reason Summary - 2 lines max */}
              <p className="text-sm text-muted-foreground line-clamp-2">
                {reason}
              </p>

              {/* Action Row */}
              <div className="flex items-center justify-between pt-1">
                <span className="text-sm font-medium text-primary">
                  {suggestedAction}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  tabIndex={-1}
                >
                  <span className="sr-only">View details</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// =============================================================================
// Skeleton Loader
// =============================================================================

/**
 * Skeleton loader for RecommendationPreviewCard
 *
 * Matches the layout of the main component for smooth loading states.
 */
export function RecommendationPreviewCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Priority Indicator Skeleton */}
          <Skeleton className="h-2 w-2 rounded-full mt-2 shrink-0" />

          {/* Content Skeleton */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Header */}
            <div className="space-y-1">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-48" />
            </div>

            {/* Reason */}
            <div className="space-y-1">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>

            {/* Action */}
            <div className="pt-1">
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// List Component
// =============================================================================

/**
 * Props for the recommendations list
 */
export interface RecommendationPreviewListProps {
  /** Array of recommendations to display */
  recommendations: RecommendationPreview[];
  /** Maximum number of items to show (default: all) */
  maxItems?: number;
  /** Additional CSS classes */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
}

/**
 * Recommendation Preview List
 *
 * Renders a list of recommendation preview cards.
 * Useful for the home screen recommendations section.
 *
 * @example
 * ```tsx
 * <RecommendationPreviewList
 *   recommendations={recommendations}
 *   maxItems={3}
 *   emptyMessage="No recommendations at this time"
 * />
 * ```
 */
export function RecommendationPreviewList({
  recommendations,
  maxItems,
  className,
  emptyMessage = "No recommendations",
}: RecommendationPreviewListProps) {
  const displayedRecommendations = maxItems
    ? recommendations.slice(0, maxItems)
    : recommendations;

  if (displayedRecommendations.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {displayedRecommendations.map((recommendation) => (
        <RecommendationPreviewCard
          key={recommendation.id}
          recommendation={recommendation}
        />
      ))}
    </div>
  );
}

/**
 * Skeleton loader for the recommendations list
 */
export function RecommendationPreviewListSkeleton({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {[...Array(count)].map((_, i) => (
        <RecommendationPreviewCardSkeleton key={i} />
      ))}
    </div>
  );
}

export default RecommendationPreviewCard;
